<template>
    <div class="animated fadeIn">
        <component-header
            title="Reconciliation Center"
            :hasSearch="false"
            :hasCreate="false"
            :hasReload="false"
            :isLoading="loading"
            class="mb-2"
        >
            <template #heading-actions>
                <div class="finance-date-filter-wrapper">
                    <eb-select
                        class="finance-date-filter"
                        type="text"
                        label="text"
                        v-model="selectedDateRange"
                        :selected="selectedDateRange.text"
                        :options="dateRangeOptions"
                        @input="onDateRangeSelect"
                    ></eb-select>
                    <div
                        v-if="filters.dateRange === 'custom' && customLabel"
                        class="small text-primary mt-1"
                    >
                        {{ customLabel }}
                    </div>
                </div>
            </template>
        </component-header>

        <custom-date-modal
            ref="customDateModal"
            modal-id="custom-date-modal"
            modal-ref="customDateModal"
            @customDateRange="setCustomDateRange"
        />

        <b-card class="card-wrap reconciliation-content" border-variant="info">
            <div v-if="hasNoData" class="no-data-full">
                <div class="no-data-content">
                    <i class="fa fa-inbox no-data-icon"></i>
                    <p class="no-data-text">No Data Available</p>
                    <p class="no-data-subtext">There are no metrics, chart data, or alerts to display. Try selecting a different date range.</p>
                </div>
            </div>
            <template v-else>
            <div class="metrics-section">
                <b-row>
                    <b-col
                        v-for="(item, index) in metrics"
                        :key="index"
                        cols="12"
                        sm="6"
                        lg="4"
                        xl="3"
                        class="mb-3"
                    >
                        <b-card
                            class="metric-card p-2"
                            :class="`metric-card--${getStatusClass(item.status).replace('badge-', '')}`"
                        >
                            <div
                                class="status-container d-flex align-items-center gap-3"
                            >
                                <div class="icon-wrapper">
                                    <i :class="item.icon"></i>
                                </div>

                                <span
                                    :class="[
                                        'status-badge',
                                        getStatusClass(item.status),
                                    ]"
                                    class="ml-2"
                                >
                                    {{ item.status }}
                                </span>
                            </div>

                            <div class="title-text mt-3">
                                {{ item.title }}
                            </div>

                            <div class="match-rate mt-3">
                                <span :class="getRateColor(item.matchRate)">
                                    {{ item.matchRate }}%
                                </span>
                                <span class="rate-label">match rate</span>
                            </div>

                            <div class="discrepancies mt-2">
                                <span class="label">Discrepancies:</span>
                                <span class="value">{{
                                    item.discrepancies
                                }}</span>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </div>
            <b-row v-if="chartData && chartData.labels && chartData.labels.length" class="mt-4">
                <b-col cols="12" md="12" class="mb-3 mb-md-0">
                    <b-card class="reconciliation-summary-card">
                        <template #header>
                            <div class="card-header-custom">
                                <i class="fa fa-bar-chart mr-2"></i>
                                <span>Reconciliation Summary</span>
                            </div>
                        </template>

                        <BarChart
                            v-if="
                                chartData &&
                                chartData.labels &&
                                chartData.labels.length
                            "
                            :labelsData="chartData.labels"
                            :chartData="chartData.datasets"
                            :chartOptions="chartOptions"
                        />
                        
                    </b-card>
                </b-col>
                
                <!-- <b-col cols="12" md="4">
                    <b-card class="reconciliation-card">
                        <h4 class="font-weight-bold mb-4">Generate Reports</h4>

                        <label class="filter-label">Report Type</label>
                        <b-form-select
                            v-model="reportType"
                            :options="reportOptions"
                            class="filter-select mb-3"
                        ></b-form-select>

                        <b-button
                            variant="primary"
                            block
                            class="py-2 generate-btn"
                            @click="generateReport"
                        >
                            <i class="fa fa-download mr-2"></i> Generate Report
                        </b-button>

                        <div class="mt-4">
                            <h6 class="text-muted mb-2">Export Links:</h6>

                            <div class="export-link mb-2">
                                <i
                                    class="fa fa-file-pdf-o text-danger mr-2"
                                ></i>
                                <a href="#" download>Daily_Report_Jan07.pdf</a>
                            </div>

                            <div class="export-link">
                                <i
                                    class="fa fa-file-excel-o text-success mr-2"
                                ></i>
                                <a href="#" download>Weekly_Summary.xlsx</a>
                            </div>
                        </div>
                    </b-card>
                </b-col> -->
            </b-row>
            
            <div v-if="alerts && alerts.length" class="mt-4 discrepancy-section">
                <div class="section-header-row mb-3">
                    <div class="section-title">
                        <i class="fa fa-exclamation-triangle mr-2"></i>
                        <span>Discrepancy Alerts</span>
                    </div>
                    <span class="active-alert-badge">
                        {{ (alerts && alerts.length) || 0 }} Active Alerts
                    </span>
                </div>
            </div>
            <div
                v-for="(alert, index) in paginatedAlerts"
                :key="index"
                class="alert-card"
            >
                <div class="alert-row">
                    <div class="alert-icon">
                        <i class="fa fa-exclamation-circle"></i>
                    </div>

                    <div class="alert-text">
                        <div class="alert-header">
                            <h5 class="alert-title">{{ alert.title }}</h5>

                            <span
                                class="priority-badge"
                                :class="getPriorityClass(alert.priority)"
                            >
                                {{ alert.priority }}
                            </span>
                        </div>

                        <p class="alert-desc">{{ alert.description }}</p>
                        <p class="alert-meta">
                            {{ alert.date }} • {{ alert.meta }}
                        </p>
                    </div>

                    <div class="alert-action">
                        <b-button variant="primary" size="sm" class="review-btn">
                            <i class="fa fa-eye mr-1"></i> Review
                        </b-button>
                    </div>
                </div>
            </div>
            <div
                v-if="alerts.length > itemsPerPage"
                class="pagination-wrapper my-4"
            >
                <b-pagination
                    v-model="currentPage"
                    :total-rows="alerts.length"
                    :per-page="itemsPerPage"
                    align="center"
                    hide-goto-end-buttons
                    prev-text="Prev"
                    next-text="Next"
                    class="justify-content-center justify-content-md-end"
                ></b-pagination>
            </div>
            </template>
        </b-card>
    </div>
</template>

<script>
import moment from "moment";
import ComponentHeader from "@/components/componentNewHeader";
import CustomDateModal from "@/components/FormElements/CustomDate.vue";
import BarChart from "@/components/chart/BarChartv2.vue";
import ebSelect from "@/components/FormElements/Select.vue";
import { mapGetters } from "vuex";

export default {
    components: {
        ComponentHeader,
        CustomDateModal,
        BarChart,
        ebSelect,   
    },

    data() {
        return {
            loading: false,
            filters: {
                dateRange: "7",
                viewMode: "Daily",
                product: "all",
                seller: "all",
                category: "all",
            },

            selectedDateRange: { value: "7", text: "Last 7 Days" },

            customStart: "",
            customEnd: "",

            dateRangeOptions: [
                { value: "7", text: "Last 7 Days" },
                { value: "30", text: "Last 30 Days" },
                { value: "custom", text: "Custom Range" },
            ],

            viewModes: ["Daily", "Weekly", "Monthly"],

            productOptions: [
                { value: "all", text: "All Products" },
                { value: "p1", text: "Product A" },
                { value: "p2", text: "Product B" },
            ],

            sellerOptions: [
                { value: "all", text: "All Sellers" },
                { value: "s1", text: "Seller A" },
                { value: "s2", text: "Seller B" },
            ],

            categoryOptions: [
                { value: "all", text: "All Categories" },
                { value: "c1", text: "Category A" },
                { value: "c2", text: "Category B" },
            ],

            reportType: "daily",

            reportOptions: [
                { value: "daily", text: "Daily Summary" },
                { value: "weekly", text: "Weekly Summary" },
                { value: "monthly", text: "Monthly Summary" },
            ],

            currentPage: 1,
            itemsPerPage: 5,

            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
            },
        };
    },

    computed: {
        ...mapGetters({
            getReconciliationDashboard:
                "fraudManagement/GetReconciliationDashboard",
            getReconciliationSummary:
                "fraudManagement/GetReconciliationSummary",
            getReconciliationRecords:
                "fraudManagement/GetReconciliationRecords",
        }),
        customLabel() {
            if (!this.customStart || !this.customEnd) return "";
            return `Custom: ${this.customStart} → ${this.customEnd}`;
        },
        hasNoData() {
            const noMetrics = !this.metrics || this.metrics.length === 0;
            const noChartLabels =
                !this.chartData?.labels || this.chartData.labels.length === 0;
            const noAlerts = !this.alerts || this.alerts.length === 0;
            return noMetrics && noChartLabels && noAlerts;
        },
        summary() {
            return this.getReconciliationSummary;
        },
        chartData() {
            const summary = this.summary;
            if (
                !summary ||
                !summary.labels ||
                !Array.isArray(summary.labels) ||
                summary.labels.length === 0
            ) {
                return {
                    labels: [],
                    datasets: [],
                };
            }
            const formattedLabels = summary.labels.map((label) => {
                if (!label) return "";
                const date = moment(label);
                return date.isValid() ? date.format("MMM DD") : label;
            });
            const datasets = [];
            if (summary.matched && Array.isArray(summary.matched)) {
                datasets.push({
                    label: "Matched",
                    backgroundColor: "#28C76F",
                    data: summary.matched,
                });
            }
            if (summary.discrepancy && Array.isArray(summary.discrepancy)) {
                datasets.push({
                    label: "Discrepancy",
                    backgroundColor: "#FF4D4D",
                    data: summary.discrepancy,
                });
            }
            if (summary.matchRate && Array.isArray(summary.matchRate)) {
                datasets.push({
                    label: "Match Rate %",
                    backgroundColor: "#0056ff",
                    data: summary.matchRate.map((rate) => {
                        const numRate =
                            typeof rate === "number" ? rate : parseFloat(rate);
                        return isNaN(numRate)
                            ? 0
                            : parseFloat(numRate.toFixed(2));
                    }),
                });
            }

            return {
                labels: formattedLabels,
                datasets: datasets,
            };
        },
        alerts() {
            const alertsData = [
                ...this.getReconciliationRecords.map((record) => ({
                    title: record.reconciliationType,
                    priority: record.precedence,
                    description: record.reason,
                    date: this.formatDate(record.createdAt),
                    meta: record.difference,
                })),
            ];
            return alertsData;
        },
        paginatedAlerts() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.alerts.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.alerts.length / this.itemsPerPage);
        },
        metrics() {
            const apiData = this.getReconciliationDashboard;
            if (!Array.isArray(apiData) || apiData.length === 0) {
                return [];
            }
            const typeMapping = {
                PLATFORM_TRANSACTIONS_VS_COLLECTION_ACCOUNT: {
                    icon: "fas fa-file-invoice",
                    title: "Platform Transactions vs Collection Account",
                },
                ORDER_PAYMENT: {
                    icon: "fa fa-user",
                    title: "Seller Payments vs Completed Orders",
                },
                PLATFORM_FEE: {
                    icon: "fa fa-dollar-sign",
                    title: "Platform Fees vs Transaction Records",
                },
                TAX: {
                    icon: "fa fa-dollar-sign",
                    title: "Tax Collected vs Recorded Transactions",
                },
                REFUNDS_VS_SELLER_BALANCES: {
                    icon: "fa fa-sync",
                    title: "Refunds vs Seller Balances",
                },
                ORDER_PLACED_VS_DELIVERED: {
                    icon: "fa fa-shopping-cart",
                    title: "Orders Placed vs Delivered",
                },
                INVENTORY_UPDATES_VS_FULFILLMENTS: {
                    icon: "fas fa-box",
                    title: "Inventory Updates vs Fulfillments",
                },
                SHIPPING_COSTS_VS_MARKETPLACE_ORDERS: {
                    icon: "fa fa-truck",
                    title: "Shipping Costs vs Marketplace Orders",
                },
            };
            const latestByType = {};
            apiData.forEach((record) => {
                const type = record.reconciliationType;
                if (
                    !latestByType[type] ||
                    new Date(record.createdAt) >
                        new Date(latestByType[type].createdAt)
                ) {
                    latestByType[type] = record;
                }
            });
            return Object.values(latestByType).map((record) => {
                const mapping = typeMapping[record.reconciliationType] || {
                    icon: "fa fa-chart-line",
                    title: record.reconciliationType.replace(/_/g, " "),
                };

                const statusMap = {
                    MATCHED: "Matched",
                    DISCREPANCY: "Discrepancy",
                    PARTIAL: "Partial",
                };

                const dashboardRecord = {
                    icon: mapping.icon,
                    title: mapping.title,
                    status: statusMap[record.status] || record.status,
                    matchRate: parseFloat(record.matchPercentage.toFixed(1)),
                    discrepancies: record.discrepancyRecords || 0,
                };
                return dashboardRecord;
            });
        },
    },

    methods: {
        formatDate(date) {
            if (!date) return "-";
            return moment(date).format("DD MMM YYYY, HH:mm");
        },
        onDateRangeSelect(option) {
            this.filters.dateRange = option.value;
            this.handleDateRangeChange(option.value);
        },

        handleDateRangeChange(val) {
            if (val === "custom") {
                this.$refs.customDateModal.openModal();
            } else {
                this.fetchReconciliationSummary();
            }
        },

        setCustomDateRange({ fromDate, toDate }) {
            this.customStart = fromDate;
            this.customEnd = toDate;
            this.filters.dateRange = "custom";
            this.fetchReconciliationSummary();
        },

        getStatusClass(status) {
            if (status === "Matched") return "badge-success";
            if (status === "Partial") return "badge-warning";
            return "badge-danger";
        },
        getRateColor(rate) {
            if (rate >= 95) return "text-primary";
            if (rate >= 85) return "text-warning";
            return "text-danger";
        },
        generateReport() {
            console.log("Generating report:", this.reportType);
        },
        getPriorityClass(level) {
            if (level === "High") return "priority-high";
            if (level === "Medium") return "priority-medium";
            return "priority-low";
        },
        async fetchReconciliationDashboardData() {
            try {
                await this.$store.dispatch(
                    "fraudManagement/fetchReconciliationDashboard",
                );
            } catch (error) {
                console.error(
                    "Error fetching reconciliation dashboard:",
                    error,
                );
            }
        },
        getDateData(dateRange) {
            if (dateRange === "custom") {
                return {
                    fromDate: this.customStart,
                    toDate: this.customEnd,
                };
            }
            if (typeof dateRange === "number" || !isNaN(dateRange)) {
                const days = parseInt(dateRange);
                return {
                    fromDate: moment()
                        .subtract(days - 1, "days")
                        .format("YYYY-MM-DD"),
                    toDate: moment().format("YYYY-MM-DD"),
                };
            }
            return {
                fromDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
                toDate: moment().format("YYYY-MM-DD"),
            };
        },
        async fetchReconciliationSummary() {
            try {
                const dateData = this.getDateData(this.filters.dateRange);
                const payload = {
                    fromDate: dateData.fromDate,
                    toDate: dateData.toDate,
                };
                const data = await this.$store.dispatch(
                    "fraudManagement/fetchReconciliationSummary",
                    payload,
                );
            } catch (error) {
                console.error("Error fetching reconciliation summary:", error);
            }
        },
        async fetchReconciliationRecords() {
            try {
                const data = await this.$store.dispatch(
                    "fraudManagement/fetchReconciliationRecords",
                );
            } catch (error) {
                console.error("Error fetching reconciliation records:", error);
            }
        },
    },
    watch: {
        "filters.viewMode"() {
            this.fetchReconciliationSummary();
        },
        "filters.product"() {
            this.fetchReconciliationDashboardData();
            this.fetchReconciliationSummary();
        },
        "filters.seller"() {
            this.fetchReconciliationDashboardData();
            this.fetchReconciliationSummary();
        },
        "filters.category"() {
            this.fetchReconciliationDashboardData();
            this.fetchReconciliationSummary();
        },
    },
    mounted() {
        this.fetchReconciliationDashboardData();
        this.fetchReconciliationSummary();
        this.fetchReconciliationRecords();
    },
};
</script>

<style scoped>
.card-wrap {
    box-shadow: 0 0 10px #ccc;
    border-radius: 12px;
    border: 1px solid var(--slate-200, #e2e8f0);
}

.reconciliation-content :deep(.card-body) {
    padding: 1.5rem;
}

.finance-date-filter-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-left: 15px;
}

::v-deep .finance-date-filter .vs__dropdown-toggle {
    min-width: 200px;
}


.card-header-custom {
    font-size: 1rem;
    font-weight: 700;
    color: var(--slate-800, #1e293b);
}

.section-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.section-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--slate-800, #1e293b);
}

.reconciliation-summary-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    height: 100%;
    border: 1px solid var(--slate-200, #e2e8f0);
}

.reconciliation-summary-card :deep(.card-header) {
    background: var(--slate-50, #f8fafc);
    border-bottom: 1px solid var(--slate-200, #e2e8f0);
    padding: 1rem 1.25rem;
    font-weight: 600;
}

.no-data-card,
.no-data-full {
    min-height: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.no-data-content {
    text-align: center;
    padding: 2rem;
}

.no-data-icon {
    font-size: 3rem;
    color: var(--slate-300, #cbd5e1);
    margin-bottom: 1rem;
}

.no-data-text {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--slate-600, #475569);
    margin-bottom: 0.5rem;
}

.no-data-subtext {
    font-size: 0.875rem;
    color: var(--slate-500, #64748b);
    margin: 0;
}

.filter-label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
}

.filter-select {
    border-radius: 10px !important;
    height: 40px;
}

.view-mode-btn {
    border-radius: 12px;
    padding: 6px 14px;
    font-size: 14px;
    border: 1px solid #e2e8f0;
}

.view-mode-btn.active {
    background-color: #0056ff !important;
    color: white !important;
    border-color: #0056ff !important;
}
.row {
    display: flex;
    flex-wrap: wrap;
}
.metric-card {
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 0 10px #ccc;
    border: 1px solid var(--slate-200, #e2e8f0);
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    padding: 1.25rem !important;
}

.metric-card :deep(.card-body) {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0;
}

.metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.metric-card--success {
    border-left: 4px solid var(--success, #10b981);
}

.metric-card--warning {
    border-left: 4px solid var(--warning, #eab308);
}

.metric-card--danger {
    border-left: 4px solid var(--danger, #ef4444);
}

.status-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
}

.icon-wrapper {
    width: 42px;
    min-width: 42px;
    height: 42px;
    background: var(--slate-100, #f1f5f9);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.icon-wrapper i {
    font-size: 20px;
    color: var(--primary, #2625bf);
}

.title-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--slate-800, #1e293b);
    margin-top: 1rem;
    min-height: 48px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.discrepancies {
    margin-top: auto;
    padding-top: 10px;
}

.match-rate {
    margin-top: 0.5rem;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.badge-success {
    background: var(--success-bg);
    color: var(--success-text);
}

.badge-warning {
    background: var(--warning-bg);
    color: var(--warning-text);
}

.badge-danger {
    background: var(--danger-bg);
    color: var(--danger-text);
}

.match-rate span:first-child {
    font-size: 1.5rem;
    font-weight: 700;
}

.rate-label {
    font-size: 0.8125rem;
    margin-left: 4px;
    color: var(--slate-500, #64748b);
}

.discrepancies .label {
    font-size: 0.875rem;
    color: var(--slate-600, #475569);
}

.discrepancies .value {
    font-size: 1rem;
    font-weight: 600;
    color: var(--danger-text, #dc2626);
    margin-left: 6px;
}

.generate-btn {
    background: #0056ff;
    border-radius: 10px;
    font-weight: 600;
}

.export-link a {
    font-size: 14px;
    color: #0056ff;
}

.export-link a:hover {
    text-decoration: underline;
}

.active-alert-badge {
    background: var(--danger-bg);
    color: var(--danger-text);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.8125rem;
    font-weight: 600;
}

.alert-card {
    background: #ffffff;
    padding: 1.25rem 1.5rem;
    border-radius: 12px;
    margin-bottom: 1rem;
    box-shadow: 0 0 10px #ccc;
    border: 1px solid var(--slate-200, #e2e8f0);
    transition: box-shadow 0.2s ease;
}

.alert-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.alert-row {
    display: grid;
    grid-template-columns: 40px 1fr auto;
    grid-column-gap: 1.25rem;
    align-items: center;
}

.alert-icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    background: var(--danger-bg);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.alert-icon i {
    font-size: 1.125rem;
    color: var(--danger-text, #dc2626);
}

.alert-text {
    display: block;
}

.alert-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.alert-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--slate-800, #1e293b);
}

.alert-desc {
    margin: 0.5rem 0.25rem 0 0;
    color: var(--slate-600, #475569);
    font-size: 0.875rem;
    line-height: 1.5;
}

.alert-meta {
    margin: 0.25rem 0 0;
    font-size: 0.8125rem;
    color: var(--slate-500, #64748b);
}

.priority-badge {
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 600;
}

.priority-high {
    background: var(--danger-bg);
    color: var(--danger-text);
}

.priority-medium {
    background: var(--warning-bg);
    color: var(--warning-text);
}

.priority-low {
    background: var(--info-bg);
    color: var(--info-text);
}

.alert-action {
    display: block;
}

.review-btn {
    border-radius: 8px;
    padding: 6px 16px;
    font-weight: 600;
    font-size: 0.8125rem;
}

.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
}
@media (min-width: 768px) {
    .pagination-wrapper {
        justify-content: flex-end;
    }
}

/* Reconciliation Summary card height */
.reconciliation-summary-card:not(.no-data-card) {
    position: relative;
    min-height: 380px;
}

/* ===== Extra-large desktops (>= 1400px) ===== */
@media (min-width: 1400px) {
    .metric-card {
        padding: 1.5rem !important;
    }

    .icon-wrapper {
        width: 48px;
        min-width: 48px;
        height: 48px;
    }

    .icon-wrapper i {
        font-size: 22px;
    }

    .title-text {
        font-size: 0.9375rem;
    }

    .match-rate span:first-child {
        font-size: 1.75rem;
    }
}

/* ===== Large desktops (1200px – 1399px) ===== */
@media (min-width: 1200px) and (max-width: 1399.98px) {
    .metric-card {
        padding: 1.125rem !important;
    }

    .title-text {
        font-size: 0.875rem;
        min-height: 44px;
    }

    .match-rate span:first-child {
        font-size: 1.5rem;
    }
}

/* ===== Small desktops / tablet landscape (992px – 1199px) ===== */
@media (min-width: 992px) and (max-width: 1199.98px) {
    .metric-card {
        padding: 1rem !important;
        min-height: 190px;
    }

    .icon-wrapper {
        width: 38px;
        min-width: 38px;
        height: 38px;
    }

    .icon-wrapper i {
        font-size: 17px;
    }

    .title-text {
        font-size: 0.8125rem;
        min-height: 40px;
    }

    .status-badge {
        padding: 3px 10px;
        font-size: 11px;
    }

    .match-rate span:first-child {
        font-size: 1.25rem;
    }

    .rate-label {
        font-size: 0.75rem;
    }

    .discrepancies .label {
        font-size: 0.8125rem;
    }

    .discrepancies .value {
        font-size: 0.875rem;
    }

    .alert-row {
        grid-template-columns: 36px 1fr auto;
        grid-column-gap: 1rem;
    }
}

/* ===== Tablets portrait (768px – 991px) ===== */
@media (min-width: 768px) and (max-width: 991.98px) {
    .reconciliation-content :deep(.card-body) {
        padding: 1.25rem;
    }

    .metric-card {
        min-height: 180px;
        padding: 1rem !important;
    }

    .icon-wrapper {
        width: 40px;
        min-width: 40px;
        height: 40px;
    }

    .icon-wrapper i {
        font-size: 18px;
    }

    .title-text {
        font-size: 0.8125rem;
        min-height: 38px;
    }

    .status-badge {
        padding: 3px 10px;
        font-size: 11px;
    }

    .match-rate span:first-child {
        font-size: 1.25rem;
    }

    .rate-label {
        font-size: 0.75rem;
    }

    .discrepancies .label {
        font-size: 0.8125rem;
    }

    .discrepancies .value {
        font-size: 0.875rem;
    }

    .alert-row {
        grid-template-columns: 36px 1fr auto;
        grid-column-gap: 1rem;
    }

    .alert-title {
        font-size: 0.9375rem;
    }

    .alert-desc {
        font-size: 0.8125rem;
    }
}

/* ===== Small tablets / large phones (576px – 767px) ===== */
@media (min-width: 576px) and (max-width: 767.98px) {
    .reconciliation-content {
        padding: 0.75rem;
    }

    .reconciliation-content :deep(.card-body) {
        padding: 1rem;
    }

    .finance-date-filter-wrapper {
        align-items: center;
        width: 100%;
        margin-left: 0;
    }

    .finance-date-filter {
        width: 100%;
        min-width: 0;
    }

    .metric-card {
        min-height: 170px;
        padding: 1rem !important;
    }

    .icon-wrapper {
        width: 38px;
        min-width: 38px;
        height: 38px;
    }

    .icon-wrapper i {
        font-size: 17px;
    }

    .title-text {
        font-size: 0.8125rem;
        min-height: 36px;
    }

    .status-badge {
        padding: 3px 10px;
        font-size: 11px;
    }

    .match-rate span:first-child {
        font-size: 1.25rem;
    }

    .rate-label {
        font-size: 0.75rem;
    }

    .discrepancies .label {
        font-size: 0.8125rem;
    }

    .discrepancies .value {
        font-size: 0.875rem;
    }

    .section-header-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .alert-card {
        padding: 1rem;
    }

    .alert-row {
        grid-template-columns: 34px 1fr;
        grid-row-gap: 10px;
    }

    .alert-action {
        grid-column: 1 / -1;
        width: 100%;
        margin-top: 4px;
    }

    .alert-icon {
        width: 34px;
        height: 34px;
        min-width: 34px;
    }

    .alert-icon i {
        font-size: 0.9375rem;
    }

    .alert-title {
        font-size: 0.9375rem;
    }

    .alert-desc {
        font-size: 0.8125rem;
    }

    .alert-meta {
        font-size: 0.75rem;
    }

    .review-btn {
        width: 100%;
        font-size: 0.8125rem;
    }
}

/* ===== Mobile phones (< 576px) ===== */
@media (max-width: 575.98px) {
    .reconciliation-content {
        padding: 0.5rem;
    }

    .reconciliation-content :deep(.card-body) {
        padding: 0.75rem;
    }

    .finance-date-filter-wrapper {
        align-items: center;
        width: 100%;
        margin-left: 0;
    }

    .finance-date-filter {
        width: 100%;
        min-width: 0;
    }

    .no-data-card,
    .no-data-full {
        min-height: 240px;
    }

    .no-data-icon {
        font-size: 2.5rem;
    }

    .no-data-text {
        font-size: 1rem;
    }

    .no-data-subtext {
        font-size: 0.8125rem;
    }

    .filter-label {
        font-size: 13px;
    }

    .filter-select {
        height: 36px;
        font-size: 13px;
        padding: 4px 8px;
    }

    .view-mode-btn {
        padding: 5px 12px;
        font-size: 13px;
        margin-right: 0.5rem;
        margin-bottom: 0.5rem;
    }

    .metric-card {
        min-height: 150px;
        padding: 0.875rem !important;
    }

    .status-container {
        flex-wrap: wrap;
        gap: 8px;
    }

    .icon-wrapper {
        width: 36px;
        min-width: 36px;
        height: 36px;
    }

    .icon-wrapper i {
        font-size: 16px;
    }

    .title-text {
        font-size: 0.75rem;
        min-height: 32px;
    }

    .status-badge {
        padding: 3px 8px;
        font-size: 10px;
    }

    .match-rate span:first-child {
        font-size: 1.125rem;
    }

    .rate-label {
        font-size: 0.6875rem;
    }

    .discrepancies .label {
        font-size: 0.75rem;
    }

    .discrepancies .value {
        font-size: 0.8125rem;
    }

    .section-header-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .alert-card {
        padding: 0.875rem;
    }

    .alert-row {
        grid-template-columns: 32px 1fr;
        grid-row-gap: 8px;
    }

    .alert-action {
        grid-column: 1 / -1;
        width: 100%;
        margin-top: 4px;
    }

    .alert-icon {
        width: 32px;
        height: 32px;
        min-width: 32px;
    }

    .alert-icon i {
        font-size: 0.875rem;
    }

    .alert-title {
        font-size: 0.875rem;
    }

    .alert-desc {
        font-size: 0.8125rem;
    }

    .alert-meta {
        font-size: 0.75rem;
    }

    .alert-header {
        flex-wrap: wrap;
    }

    .priority-badge {
        font-size: 0.6875rem;
        padding: 3px 8px;
    }

    .review-btn {
        width: 100%;
        padding: 6px 16px;
        font-size: 0.8125rem;
        white-space: normal;
    }

    .reconciliation-summary-card:not(.no-data-card) {
        min-height: 280px;
    }
}
</style>
